import { useState } from 'react';
import { MainLayout } from '../components/MainLayout';
import { OfficeProfileForm } from '../components/settings/OfficeProfileForm';
import { ProviderList } from '../components/settings/ProviderList';
import { ProviderForm, type ProviderFormData } from '../components/settings/ProviderForm';
import { SubscriptionInfo } from '../components/settings/SubscriptionInfo';
import { MyHealthSettings } from '../components/settings/MyHealthSettings';
import {
  useOfficeQuery,
  useProvidersQuery,
  updateOffice,
  createProvider,
  updateProvider,
  updateSubscription,
  type UpdateOfficeData,
} from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import type { DentalOfficeProvider } from '../types';

type Tab = 'profile' | 'providers' | 'subscription' | 'myhealth';

export function SettingsPage() {
  const { token, provider, office: userOffice } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<DentalOfficeProvider | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const officeId = userOffice?.id || 0;
  const { data: office, isLoading: isLoadingOffice, refetch: refetchOffice } = useOfficeQuery(officeId);
  const { data: providers = [], isLoading: isLoadingProviders, refetch: refetchProviders } = useProvidersQuery(officeId);

  const isAdmin = provider?.role === 'admin';

  const handleUpdateOffice = async (data: UpdateOfficeData) => {
    if (!token || !office) return;

    setIsSubmitting(true);
    try {
      await updateOffice(office.id, data, token);
      await refetchOffice();
      alert('Stillingar uppfærðar');
    } catch (error) {
      console.error('Failed to update office:', error);
      alert('Villa kom upp við að uppfæra stillingar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateProvider = () => {
    setEditingProvider(null);
    setShowProviderForm(true);
  };

  const handleEditProvider = (provider: DentalOfficeProvider) => {
    setEditingProvider(provider);
    setShowProviderForm(true);
  };

  const handleDeleteProvider = async (provider: DentalOfficeProvider) => {
    if (!token || !office) return;
    if (!confirm(`Ertu viss um að þú viljir eyða ${provider.name}?`)) {
      return;
    }

    try {
      await updateProvider(
        office.id,
        provider.id,
        { is_active: false },
        token
      );
      await refetchProviders();
    } catch (error) {
      console.error('Failed to delete provider:', error);
      alert('Villa kom upp við að eyða starfsmanni');
    }
  };

  const handleProviderSubmit = async (data: ProviderFormData) => {
    if (!token || !office) return;

    setIsSubmitting(true);
    try {
      if (editingProvider) {
        // For updates, password is optional
        const updateData = { ...data };
        if (!updateData.password) {
          delete (updateData as Partial<ProviderFormData>).password;
        }
        await updateProvider(office.id, editingProvider.id, updateData, token);
      } else {
        // For creation, password is required
        if (!data.password) {
          alert('Lykilorð er áskilið fyrir nýjan starfsmann');
          return;
        }
        await createProvider(office.id, data, token);
      }
      await refetchProviders();
      setShowProviderForm(false);
      setEditingProvider(null);
    } catch (error) {
      console.error('Failed to save provider:', error);
      alert('Villa kom upp við að vista starfsmann');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProviderCancel = () => {
    setShowProviderForm(false);
    setEditingProvider(null);
  };

  const handleUpdateSubscription = async (data: { subscription_tier?: string; billing_cycle?: string }) => {
    if (!token || !office) return;

    if (!confirm(`Ertu viss um að þú viljir breyta áskrift?`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const validData: {
        subscription_tier?: 'starter' | 'professional' | 'enterprise';
        billing_cycle?: 'monthly' | 'annual';
      } = {};

      if (data.subscription_tier) {
        validData.subscription_tier = data.subscription_tier as 'starter' | 'professional' | 'enterprise';
      }
      if (data.billing_cycle) {
        validData.billing_cycle = data.billing_cycle as 'monthly' | 'annual';
      }

      await updateSubscription(office.id, validData, token);
      await refetchOffice();
      alert('Áskrift uppfærð');
    } catch (error) {
      console.error('Failed to update subscription:', error);
      alert('Villa kom upp við að uppfæra áskrift');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMyHealthToggle = async (enabled: boolean) => {
    console.log('MyHealth integration:', enabled ? 'enabled' : 'disabled');
    alert('MyHealth samþætting ' + (enabled ? 'virkjuð' : 'óvirkjuð'));
  };

  const handleTestConnection = async () => {
    console.log('Testing MyHealth connection...');
    alert('Tenging virkar!');
  };

  const tabs = [
    { id: 'profile' as Tab, label: 'Prófíll' },
    { id: 'providers' as Tab, label: 'Starfsfólk' },
    { id: 'subscription' as Tab, label: 'Áskrift' },
    { id: 'myhealth' as Tab, label: 'MyHealth' },
  ];

  // Count linked patients (mock for now)
  const linkedPatientsCount = 0;

  if (isLoadingOffice) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Hleð stillingum...</p>
        </div>
      </MainLayout>
    );
  }

  if (!office) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-red-600">Ekki tókst að hlaða stillingum</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Stillingar</h1>
          <p className="text-gray-600">Stjórna stofnun og notendum</p>
        </div>

        {/* Provider Form Modal */}
        {showProviderForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingProvider ? 'Breyta starfsmanni' : 'Nýr starfsmaður'}
              </h2>
              <ProviderForm
                provider={editingProvider || undefined}
                onSubmit={handleProviderSubmit}
                onCancel={handleProviderCancel}
                isLoading={isSubmitting}
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Prófíll stofnunar</h2>
              <OfficeProfileForm
                office={office}
                onSubmit={handleUpdateOffice}
                isLoading={isSubmitting}
              />
            </div>
          )}

          {activeTab === 'providers' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Starfsfólk</h2>
                {isAdmin && (
                  <button
                    onClick={handleCreateProvider}
                    className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    + Nýr starfsmaður
                  </button>
                )}
              </div>

              {isLoadingProviders ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <ProviderList
                  providers={providers}
                  onEdit={handleEditProvider}
                  onDelete={handleDeleteProvider}
                  canManage={isAdmin}
                />
              )}
            </div>
          )}

          {activeTab === 'subscription' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Áskrift</h2>
              <SubscriptionInfo
                office={office}
                onUpdate={handleUpdateSubscription}
                canManage={isAdmin}
              />
            </div>
          )}

          {activeTab === 'myhealth' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">MyHealth Samþætting</h2>
              <MyHealthSettings
                isEnabled={false}
                linkedPatientsCount={linkedPatientsCount}
                onToggle={handleMyHealthToggle}
                onTestConnection={handleTestConnection}
              />
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
